import { useQuery } from "@tanstack/react-query";
import { diffWords } from "diff";
import { useSelector } from "react-redux";

import { api } from "../lib/api";
import type { RootState } from "../../store/store";
import type {
	IRiskCategoryData,
	IMitigation,
	IAnalysisStore,
	IKeyTermMitigations,
} from "../../store/playbook-analysis-slice";

interface IPlaybookAPIResponse {
	missing: Array<IKeyTermMitigations>;
	contradictory: Array<IKeyTermMitigations>;
}

export function useAnalyzePlaybook(isDataAvailable: boolean) {
	const contractId = useSelector((state: RootState) => state.files.fileId);

	return useQuery({
		queryKey: ["analyze-playbook"],
		queryFn: async () => {
			const analysis: any = await api.post("api/v1/playbook/analyze-playbook", {
				contract_id: contractId,
			});

			if (!analysis.success) {
				throw new Error("Playbook analysis failed");
			}

			return processPlaybookAPIData({
				missing: analysis.missing,
				contradictory: analysis.contradictory,
			});
		},
		enabled: !isDataAvailable,
		retry: false,
	});
}

function processPlaybookAPIData({ missing, contradictory }: IPlaybookAPIResponse): IAnalysisStore {
	const contradictoryStore: IRiskCategoryData = { riskCount: 0, risks: [] };
	const missingStore: IRiskCategoryData = { riskCount: 0, risks: [] };
	const processedStore: IRiskCategoryData = { riskCount: 0, risks: [] };

	contradictory.forEach((keyTerm) => {
		const contradictoryMitigations: IMitigation[] = [];
		const processedMitigations: IMitigation[] = [];

		keyTerm.values.forEach((mitigation) => {
			mitigation.diff = diffWords(mitigation.reference, mitigation.new_version);

			// this regex will match alphanumeric and mentioned symbols and will remove other characters fully
			// We were getting bug in UI where it will show `Unicode` characters on the mitigation card
			mitigation.standard_text = mitigation.standard_text.map((s) => s.replace(/[^a-zA-Z0-9&().,\\\-_:;!?'"/\s]/g, ""));

			if (mitigation.accepted) {
				processedStore.riskCount += 1;
				processedMitigations.push(mitigation);
				return;
			}

			contradictoryStore.riskCount += 1;
			contradictoryMitigations.push(mitigation);
		});

		if (contradictoryMitigations.length > 0) {
			contradictoryStore.risks.push({
				key: keyTerm.key,
				values: contradictoryMitigations,
			});
		}

		if (processedMitigations.length > 0) {
			processedStore.risks.push({
				key: keyTerm.key,
				values: processedMitigations,
			});
		}
	});

	missing.forEach((keyTerm) => {
		const missingMitigations: IMitigation[] = [];
		const processedMitigations: IMitigation[] = [];

		keyTerm.values.forEach((mitigation) => {
			// this regex will match alphanumeric and mentioned symbols and will remove other characters fully
			// We were getting bug in UI where it will show `Unicode` characters on the mitigation card
			mitigation.standard_text = mitigation.standard_text.map((s) => s.replace(/[^a-zA-Z0-9&().,\\\-_:;!?'"/\s]/g, ""));

			// for missing term we just suggest the standard_answer from their playbook
			// const new_version = mitigation.standard_text.at(0);
			// mitigation.diff = diffWords("", new_version);
			mitigation.diff = diffWords(mitigation.reference, mitigation.new_version);

			if (mitigation.accepted) {
				processedStore.riskCount += 1;
				processedMitigations.push(mitigation);
				return;
			}

			missingStore.riskCount += 1;
			missingMitigations.push(mitigation);
		});

		if (missingMitigations.length > 0) {
			missingStore.risks.push({
				key: keyTerm.key,
				values: missingMitigations,
			});
		}

		if (processedMitigations.length > 0) {
			processedStore.risks.push({
				key: keyTerm.key,
				values: processedMitigations,
			});
		}
	});

	return {
		contradictory: contradictoryStore,
		missing: missingStore,
		processed: processedStore,
	};
}
